<script setup lang="ts">
import { computed, onMounted, useEventListener, useTemplateRef, useElementSize, whenever, definePageMeta, usePreviewBridge, useGame, useRewardTerms } from '#imports'
import type { Promotion } from '@promotion/types'
import { noop } from 'es-toolkit'
// Namespace import, not `{ z }`: Vite drops the re-exported `z` binding, so the
// named form resolves to undefined under the Nuxt test environment.
import * as z from 'zod/v4'

definePageMeta({
    layout: false,
    hideOverlayContainer: true,
    preview: true,
})

const PREVIEW_WIDTH = '414px'
const INITIAL_HEIGHT = '600px'

interface PreviewBonusCardData {
    isActive: boolean
    reward: Promotion
}

/**
 * Mirrored by backoffice-core's `buildPreviewBonusCardPayload.ts` — the keys
 * below are the contract between the two, so update both together.
 */
const DataSchema: z.ZodType<PreviewBonusCardData> = z.looseObject({
    isActive: z.boolean().default(false),
    reward: z.looseObject({
        external_id: z.string(),
        foreign_model: z.string(),
        foreign_model_id: z.union([z.string(), z.number()]).nullable(),
        id: z.string(),
        max_reclaimable_interval_iterations: z.string().nullable(),
        start_at: z.string(),
        trigger_type: z.union([z.string(), z.number()]),
        type: z.string(),
        requirement_type: z.string(),
        reward_image_path: z.string(),
        name: z.string().optional(),
        status: z.string().optional(),
        claimed_at: z.string().optional(),
        expires_at: z.string().optional(),
        requirement: z.string().optional(),
        requirement_message: z.string().optional(),
        terms_hyperlink: z.string().optional(),
        redeem_days: z.number().optional(),
        completion_days: z.number().optional(),
        turnover_multiplier: z.union([z.string(), z.number()]).nullish().transform(value => value ?? undefined),
        turnover_requirement: z.string().optional(),
        turnover_progress: z.string().optional(),
        combined_games: z.array(z.looseObject({
            amount: z.union([z.string(), z.number()]).transform(String),
            slug: z.string(),
            name: z.string().default(''),
        })).optional(),
        staff_only: z.boolean().optional(),
    }),
})

const { data, send } = usePreviewBridge(DataSchema)

const { constructTermsForReward } = useRewardTerms()
const { getGameBySlug } = useGame()

const parsed = computed(() => data.value?.success ? data.value.data : undefined)
const gameSlug = computed(() => parsed.value?.reward.combined_games?.[0]?.slug ?? '')
const game = getGameBySlug(gameSlug, { lazy: true })

const reward = computed<Promotion | undefined>(() => {
    const parsedReward = parsed.value?.reward
    if (!parsedReward) return undefined

    const [combinedGame, ...rest] = parsedReward.combined_games ?? []
    if (!combinedGame) return parsedReward

    return {
        ...parsedReward,
        combined_games: [{ ...combinedGame, name: game.data.value?.name ?? combinedGame.slug }, ...rest],
    }
})

const content = useTemplateRef<HTMLDivElement>('content')
// Border-box: the wrapper padding must be part of the reported height.
const { height } = useElementSize(content, undefined, { box: 'border-box' })

function sendResize(height: string) {
    send({ type: 'resize', payload: { height, width: PREVIEW_WIDTH } })
}

whenever(height, h => sendResize(`${h}px`))

onMounted(() => sendResize(INITIAL_HEIGHT))

if (import.meta.client) {
    useEventListener(window, 'click', (event: MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
    }, { capture: true })
}
</script>

<template>
    <UIApp>
        <div v-if="!data">
            <UILoader bg>
                Waiting for data
            </UILoader>
        </div>

        <template v-else>
            <div v-if="data.error">
                <pre>{{ data.error.issues }}</pre>
            </div>

            <div v-else-if="reward" ref="content" class="preview-bonus-card">
                <BonusCard
                    :reward="reward"
                    :is-active="data.data.isActive"
                    :dialog="undefined"
                    :waiting-user-action="false"
                    :handle-activation="noop"
                    :construct-terms-for-reward="constructTermsForReward"
                />
            </div>
        </template>
    </UIApp>
</template>

<style scoped lang="scss">
.preview-bonus-card {
    padding: 20px;
    box-sizing: border-box;
}
</style>
